import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import {
  CALLABLES,
  type SubscriptionUploadSourceInput,
  type SubscriptionGetDashboardInput,
  type SubscriptionUploadSourceResponse,
  type SubscriptionRefreshResult,
  type SubscriptionGetDashboardResponse,
  type SubscriptionHealthRefreshResponse,
  type SubscriptionHealthGetResponse,
  type SubscriptionStatsRefreshResponse,
  type SubscriptionStatsGetResponse,
  type SubscriptionChurnListQuery,
  type SubscriptionChurnListResponse,
  type SubscriptionPromotionsGetResponse,
  type SubscriptionPromotionsRefreshResponse,
} from '@daaily/shared';

// Parsing the ~90k-row Stripe export + computing can take a while — give it the
// server's full budget.
export const callSubscriptionUploadSource = httpsCallable<SubscriptionUploadSourceInput, SubscriptionUploadSourceResponse>(
  functions, CALLABLES.SUBSCRIPTION_UPLOAD_SOURCE, { timeout: 540000 },
);
export const callSubscriptionGetDashboard = httpsCallable<SubscriptionGetDashboardInput, SubscriptionGetDashboardResponse>(
  functions, CALLABLES.SUBSCRIPTION_GET_DASHBOARD,
);
// Pull from Stripe + recompute. The server waits ~45 s for Stripe, then returns
// { status: 'pending' } (finished by the subscriptionRefreshFinisher cron); the
// long client timeout covers download + compute once Stripe is done.
export const callSubscriptionRefresh = httpsCallable<{ year: number }, SubscriptionRefreshResult>(
  functions, CALLABLES.SUBSCRIPTION_REFRESH, { timeout: 540000 },
);
// The single "Refresh data" run for every tab (revenue → subscribers → stats).
// Progress is watched on /subscriptionRefreshAll/latest; the long timeout only
// avoids a spurious client-side deadline while a slow Stripe run finishes.
export const callSubscriptionRefreshAll = httpsCallable<Record<string, never>, { joined: boolean; status: string }>(
  functions, CALLABLES.SUBSCRIPTION_REFRESH_ALL, { timeout: 1_200_000 },
);
// Health snapshot — fetches the whole subscription book from the Stripe core API.
export const callSubscriptionHealthRefresh = httpsCallable<Record<string, never>, SubscriptionHealthRefreshResponse>(
  functions, CALLABLES.SUBSCRIPTION_HEALTH_REFRESH, { timeout: 540000 },
);
export const callSubscriptionChurnList = httpsCallable<SubscriptionChurnListQuery, SubscriptionChurnListResponse>(
  functions,
  CALLABLES.SUBSCRIPTION_CHURN_LIST,
);
export const callSubscriptionHealthGet = httpsCallable<Record<string, never>, SubscriptionHealthGetResponse>(
  functions, CALLABLES.SUBSCRIPTION_HEALTH_GET,
);
// Funnel/growth stats (BigQuery) — cheap to recompute, default timeout is fine.
export const callSubscriptionStatsRefresh = httpsCallable<Record<string, never>, SubscriptionStatsRefreshResponse>(
  functions, CALLABLES.SUBSCRIPTION_STATS_REFRESH,
);
export const callSubscriptionStatsGet = httpsCallable<Record<string, never>, SubscriptionStatsGetResponse>(
  functions, CALLABLES.SUBSCRIPTION_STATS_GET,
);
// Conversion moved to analytics-service (Sep 2026) — see
// web/src/subscriptions/SubscriptionsConversionPage.tsx, which calls it
// directly via callAny() from web/src/analytics/AnalyticsPages.tsx.
// Promo-code usage (Piano) — snapshot read + standalone admin refresh (also step 4 of Refresh data).
export const callSubscriptionPromotionsGet = httpsCallable<Record<string, never>, SubscriptionPromotionsGetResponse>(
  functions, CALLABLES.SUBSCRIPTION_PROMOTIONS_GET,
);
export const callSubscriptionPromotionsRefresh = httpsCallable<Record<string, never>, SubscriptionPromotionsRefreshResponse>(
  functions, CALLABLES.SUBSCRIPTION_PROMOTIONS_REFRESH, { timeout: 300000 },
);
